import { faTriangleExclamation } from "@fortawesome/pro-light-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import { usePermission, useUser } from "../auth";
import { DataExportSettings } from "../components/NotificationSettings/DataExportSettings";
import { EmissionSettings } from "../components/NotificationSettings/EmissionSettings";
import { NotificationGroupSettings } from "../components/NotificationSettings/GroupSettings";
import { SUPPORT_EMAIL } from "../constants/globals";
import { useAdminApiClient } from "../hooks";
import { DashboardContainer, DashboardHeader } from "../ui/DashboardComponents";

export const NoEditPermissionNotice = () => (
    <div className="flex w-full items-center gap-6 rounded bg-yellow-600 px-4 py-4 text-sm text-white">
        <FontAwesomeIcon icon={faTriangleExclamation} className="h-6" />

        <div>
            <p className="mb-2 text-sm">
                You don't have permissions to edit data on this page.
            </p>
            <p>
                You can view the current values, but editing actions are
                unavailable.
            </p>
            <p>
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="mr-1 font-semibold underline"
                >
                    Contact us
                </a>
                to request permissions.
            </p>
        </div>
    </div>
);

interface AutosaveIndicatorProps {
    saved: boolean;
    error?: boolean;
}

const AutosaveIndicator = (props: AutosaveIndicatorProps) =>
    props.error ? (
        <div className="self-center rounded bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
            <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mr-2 h-3.5 w-3.5"
            />
            Changes could not be saved
        </div>
    ) : props.saved ? (
        <div className="self-center rounded bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            <FontAwesomeIcon icon={faCheck} className="mr-2 h-3.5 w-3.5" />
            Saved
        </div>
    ) : (
        <div className="self-center whitespace-nowrap text-sm text-slate-500">
            <FontAwesomeIcon icon={faCheck} className="mr-2 h-3.5 w-3.5" />
            Changes save automatically
        </div>
    );

export const NotificationSettingsPage = () => {
    useDocumentTitle("Notification Settings - AERSHED");

    const adminApiClient = useAdminApiClient();
    const { isSuperuser } = useUser();
    const canEditNotifications = usePermission("notificationsEdit");
    const [company, setCompany] = useState<number>(undefined);
    const [emissionChangesSaved, setEmissionChangesSaved] = useState(false);
    const [emissionChangesFailed, setEmissionChangesFailed] = useState(false);
    const [dataExportChangesSaved, setDataExportChangesSaved] = useState(false);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<number>();

    const companyListQuery = useQuery(
        ["staffCompanyList"],
        () => adminApiClient.adminAccountsCompaniesList(),
        {
            enabled: isSuperuser,
        },
    );

    useEffect(() => {
        if (company === undefined && companyListQuery.data?.length) {
            setCompany(companyListQuery.data[0].id);
        }
    }, [company, companyListQuery.data]);

    useEffect(() => {
        setEmissionChangesSaved(false);
        setEmissionChangesFailed(false);
        setDataExportChangesSaved(false);
        setIsGroupModalOpen(false);
        setSelectedGroup(undefined);
    }, [company]);

    const openNewGroup = () => {
        setSelectedGroup(undefined);
        setIsGroupModalOpen(true);
    };

    const openEditGroup = (groupId: number) => {
        setSelectedGroup(groupId);
        setIsGroupModalOpen(true);
    };

    const closeGroupModal = () => {
        setSelectedGroup(undefined);
        setIsGroupModalOpen(false);
    };

    useEffect(() => {
        if (!emissionChangesSaved) return;

        const timeout = window.setTimeout(
            () => setEmissionChangesSaved(false),
            3_000,
        );
        return () => window.clearTimeout(timeout);
    }, [emissionChangesSaved]);

    useEffect(() => {
        if (!emissionChangesFailed) return;

        const timeout = window.setTimeout(
            () => setEmissionChangesFailed(false),
            3_000,
        );
        return () => window.clearTimeout(timeout);
    }, [emissionChangesFailed]);

    useEffect(() => {
        if (!dataExportChangesSaved) return;

        const timeout = window.setTimeout(
            () => setDataExportChangesSaved(false),
            3_000,
        );
        return () => window.clearTimeout(timeout);
    }, [dataExportChangesSaved]);

    const isCompanyLoading = isSuperuser && companyListQuery.isLoading;
    const isEditingDisabled =
        !canEditNotifications ||
        (isSuperuser &&
            (company === undefined ||
                companyListQuery.isLoading ||
                companyListQuery.isError));

    return (
        <main className="h-full overflow-y-auto overscroll-none bg-ae-gray-100 px-10 py-8 md:px-16 lg:px-20 xl:px-24">
            <div className="mx-auto max-w-7xl">
                <header className="mb-4 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                            Notification settings
                        </h1>
                        <p className="mt-1 text-base text-slate-500">
                            Choose who gets notified and when.
                        </p>
                    </div>

                    {isSuperuser && (
                        <div className="flex items-center gap-3 rounded border border-gray-300 bg-white p-2 shadow-sm">
                            <span className="rounded bg-amber-50 px-2 py-1 text-sm font-bold text-amber-800">
                                SUPERUSER
                            </span>

                            <span className="text-sm text-slate-500">
                                Editing
                            </span>

                            <select
                                aria-label="Company"
                                className="min-w-56 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-slate-100"
                                value={company ?? ""}
                                disabled={isCompanyLoading}
                                onChange={(event) =>
                                    setCompany(Number(event.target.value))
                                }
                            >
                                {isCompanyLoading && (
                                    <option>Loading companies…</option>
                                )}

                                {companyListQuery.data?.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </header>

                {companyListQuery.isError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        Unable to load the company list. Please refresh and try
                        again.
                    </div>
                )}

                <div className="flex flex-col">
                    {!canEditNotifications && <NoEditPermissionNotice />}

                    <DashboardContainer paddingClassName="px-0 pt-0 pb-2">
                        <DashboardHeader
                            title="Notification groups"
                            description="People and external emails who receive alerts together."
                            actions={
                                isEditingDisabled
                                    ? undefined
                                    : [
                                          {
                                              label: (
                                                  <span className="flex items-center gap-2">
                                                      <PlusIcon className="h-5 w-5" />
                                                      New group
                                                  </span>
                                              ),
                                              fn: openNewGroup,
                                          },
                                      ]
                            }
                        />
                        <NotificationGroupSettings
                            company={company}
                            disabled={isEditingDisabled}
                            isModalOpen={isGroupModalOpen}
                            selectedGroup={selectedGroup}
                            onEdit={openEditGroup}
                            onModalClose={closeGroupModal}
                        />
                    </DashboardContainer>

                    <DashboardContainer paddingClassName="px-0 py-2">
                        <DashboardHeader
                            title="Emission alerts"
                            description="Choose how often each type of emission alert is sent, and to whom."
                            trailing={
                                <AutosaveIndicator
                                    saved={emissionChangesSaved}
                                    error={emissionChangesFailed}
                                />
                            }
                        />
                        <EmissionSettings
                            company={company}
                            disabled={isEditingDisabled}
                            onSaveStateChange={setEmissionChangesSaved}
                            onSaveErrorChange={setEmissionChangesFailed}
                        />
                    </DashboardContainer>

                    <DashboardContainer paddingClassName="px-0 py-2">
                        <DashboardHeader
                            title="Data export alerts"
                            description="Get an email when someone exports emission data as a CSV."
                            trailing={
                                <AutosaveIndicator
                                    saved={dataExportChangesSaved}
                                />
                            }
                        />
                        <DataExportSettings
                            company={company}
                            disabled={isEditingDisabled}
                            onSaveStateChange={setDataExportChangesSaved}
                        />
                    </DashboardContainer>
                </div>
            </div>
        </main>
    );
};
